import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { drawingService } from '../../../services/drawingService';

const DISCIPLINE_COLORS = {
  architectural: 'bg-blue-500/10 text-blue-600',
  structural: 'bg-orange-500/10 text-orange-600',
  electrical: 'bg-yellow-500/10 text-yellow-700',
  mechanical: 'bg-purple-500/10 text-purple-600',
  plumbing: 'bg-cyan-500/10 text-cyan-700',
  civil: 'bg-green-500/10 text-green-700',
  fire_protection: 'bg-red-500/10 text-red-600',
  landscape: 'bg-emerald-500/10 text-emerald-700',
  other: 'bg-muted text-muted-foreground',
};

const DISCIPLINE_LABELS = {
  architectural: 'Arch',
  structural: 'Struct',
  electrical: 'Elec',
  mechanical: 'Mech',
  plumbing: 'Plumb',
  civil: 'Civil',
  fire_protection: 'Fire',
  landscape: 'Land',
  other: 'Other',
};

const DrawingThumbnail = ({ drawing }) => {
  const [thumbUrl, setThumbUrl] = useState(null);
  const [loadingThumb, setLoadingThumb] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!drawing?.file_path) {
        setLoadingThumb(false);
        return;
      }
      // Only attempt signed URL for image types
      if (drawing?.mime_type?.startsWith('image/')) {
        const { url } = await drawingService?.getDrawingFileUrl(drawing?.file_path);
        if (!cancelled) {
          setThumbUrl(url || null);
        }
      }
      if (!cancelled) setLoadingThumb(false);
    };
    load();
    return () => { cancelled = true; };
  }, [drawing?.file_path, drawing?.mime_type]);

  const isPdf = drawing?.mime_type?.includes('pdf');

  if (loadingThumb) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted animate-pulse">
        <Icon name="FileImage" size={28} className="text-muted-foreground/40" />
      </div>
    );
  }

  if (thumbUrl) {
    return (
      <img
        src={thumbUrl}
        alt={drawing?.drawing_name}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    );
  }

  // Placeholder for PDF or unknown
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-muted/60 to-muted gap-2">
      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10">
        <Icon name={isPdf ? 'FileText' : 'FileImage'} size={24} color="var(--color-accent)" />
      </div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {isPdf ? 'PDF' : 'Drawing'}
      </span>
    </div>
  );
};

const DrawingGalleryCard = ({ drawing }) => {
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const disciplineClass =
    DISCIPLINE_COLORS?.[drawing?.discipline] || DISCIPLINE_COLORS?.other;
  const disciplineLabel =
    DISCIPLINE_LABELS?.[drawing?.discipline] || drawing?.discipline || 'Other';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/drawing-detail/${drawing?.id}`)}
      onKeyDown={e => e?.key === 'Enter' && navigate(`/drawing-detail/${drawing?.id}`)}
      className="group bg-card border border-border rounded-xl overflow-hidden shadow-elevation-2 hover:shadow-elevation-3 hover:border-accent/40 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
    >
      {/* Thumbnail */}
      <div className="relative h-40 overflow-hidden bg-muted">
        <DrawingThumbnail drawing={drawing} />

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 rounded-full p-2 shadow-lg">
            <Icon name="Eye" size={18} color="var(--color-accent)" />
          </div>
        </div>

        {/* Latest badge */}
        {drawing?.is_latest && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md text-xs font-semibold bg-success/90 text-white shadow">
            Latest
          </span>
        )}

        {/* Discipline badge */}
        {drawing?.discipline && (
          <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-semibold ${disciplineClass} shadow`}>
            {disciplineLabel}
          </span>
        )}
      </div>

      {/* Card body */}
      <div className="p-3">
        <h3 className="font-semibold text-foreground text-sm leading-tight mb-1 line-clamp-2 group-hover:text-accent transition-colors">
          {drawing?.drawing_name || 'Untitled Drawing'}
        </h3>

        {drawing?.drawing_number && (
          <p className="text-xs text-muted-foreground font-mono mb-2 truncate">
            #{drawing?.drawing_number}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 mt-2">
          {/* Revision */}
          <div className="flex items-center gap-1">
            <Icon name="GitBranch" size={12} className="text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {drawing?.revision || 'R0'}
            </span>
          </div>

          {/* Upload date */}
          <div className="flex items-center gap-1">
            <Icon name="Calendar" size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {formatDate(drawing?.upload_date || drawing?.created_at)}
            </span>
          </div>
        </div>

        {/* Site name if present */}
        {drawing?.site_name && (
          <p className="mt-2 text-xs text-muted-foreground truncate flex items-center gap-1">
            <Icon name="MapPin" size={11} className="flex-shrink-0" />
            {drawing?.site_name}
          </p>
        )}
      </div>
    </div>
  );
};

export default DrawingGalleryCard;
